import * as React from "react";
import Confirm from "components/dialog/confirm";
import { $, merge } from "glamor";
import { formatMessage } from "lib/globalize-util";
import { FormatMessage } from "react-globalize";
import { Form } from "formsy-react";
import Checkbox from "material-ui/Checkbox";
import RadioButton from "material-ui/RadioButton";
import TextField from "components/form/text-field";
import DatePicker from "components/form/date-picker";
import Select from "components/form/select-field";
import theme from "styles/theme";
import { IAllocationDimension } from "helpers/allocation-dimension";
import { Dropdown, Theme, Fabric } from "@nuorder/components";
import MenuItem from "material-ui/MenuItem";
import moment from "lib/moment-i18n";
import { GridApi } from "ag-grid-community";
import { ALLOCATION_DIMENSION_ID_PREFIX, ValueTypes, ValidationModes, Item } from "constants/product-collection";
import { safeParseInt } from "lib/number";
import { FOLDERS } from "constants/integration/folders";
import { DATATEST } from "constants/integration";
import { amphibiousGetIn } from "helpers/objUtils";

export enum AllowedExportTypeOptions {
    PURCHASE_ORDER = "PURCHASE_ORDER",
    ITEM = "ITEM",
    AFFINITY = "AFFINITY",
}
export interface IProductCollectionExportToMerchantModalProps {
    exportToMerchantDialogOpen: boolean;
    onConfirmClose: () => void;
    confirmLabel?: string;
    onSuccess?: () => void;
    merchantId: string;
    fetchMerchantAllocationDimensions: (merchantId) => void;
    merchantAllocationDimensions?: IAllocationDimension[];
    onBulkItemsClearAll: () => void;
    gridApi?: GridApi;
    collectionId: string;
    merchantFeaturePreference: any;
    onCreateCollectionExportItemsPO: (params: any) => void;
    onValidateItems?: (
        mode: ValidationModes | undefined,
        highlightCells: boolean
    ) => { validatedItems; erroredItems; warnedItems };
    onExportItemsCollection: (params: any) => void;
    exportOptions?: { item: { name: string; active: boolean }; po: { name: string; active: boolean } };
    hasExportLogs?: boolean;
    exportItemPO?: { item: { name: string; active: boolean }; po: { name: string; active: boolean } };
    onFetchItemPO?: (status: any) => void;
    onFetchMoreRows?: () => void;
}

const styles = merge(
    $(".exportToMerchantContainer", {
        minHeight: 250,
    }),
    $(" .optionsCheck", {
        margin: "10px",
    }),
    $(" .subTitle", {
        fontSize: "10px",
    }),
    $(" .formGroupContainer", {
        display: "flex",
    }),

    $(" .formGroupContainer > div", {
        width: "100%",
        display: "flex",
        padding: "0 15px 0 15px",
        flexDirection: "column !important",
    }),
    $(" .optionsCheck", {
        margin: "10px",
    }),
    $(" p.message", {
        padding: "5px 20px",
        fontSize: "12px",
    }),
    $(" p.subtitle", {
        fontSize: "12px",
    }),
    $(" .radioButton", {
        marginTop: "15px",
    }),
    $(" .title", {
        color: `${theme.palette.textColor} !important`,
        fontWeight: "bold !important",
        fontSize: "12px !important",
        marginTop: "20px !important",
        marginBottom: "4px !important",
    }),
    $(" .selectTitle", {
        color: `${theme.palette.textColor} !important`,
        fontWeight: "bold !important",
        fontSize: "12px !important",
        marginTop: "20px !important",
        marginBottom: "4px !important",
    })
);

const muiStyles = {
    titleContent: {
        display: "flex",
        alignItems: "center",
    },
    subMenuItem: {
        minWidth: 150,
    },
    labelTitle: {
        color: theme.palette.textColor,
        fontWeight: "bold",
        fontSize: "12px",
        margin: "20px 0 0 0",
    },
};

enum CreationIndicator {
    CREATE_EXPORT = "Create",
    UPDATE_EXPORT = "Update",
}

export const validateItems = ({ createItems, createPO, allowUpdate, hasExportLogs, exportItemPO }) =>
    createItems && !createPO && (!allowUpdate || !hasExportLogs || !exportItemPO);

export const ProductCollectionExportToMerchantModal: React.FC<IProductCollectionExportToMerchantModalProps> = ({
    exportToMerchantDialogOpen,
    onConfirmClose,
    merchantAllocationDimensions,
    fetchMerchantAllocationDimensions,
    merchantId,
    onBulkItemsClearAll,
    gridApi,
    collectionId,
    merchantFeaturePreference,
    onCreateCollectionExportItemsPO,
    onValidateItems,
    onExportItemsCollection,
    exportOptions,
    hasExportLogs,
    onFetchMoreRows,
    exportItemPO,
    onFetchItemPO,
}) => {
    const selectedOptions = React.useRef({});
    const [activeStep, setActiveStep] = React.useState<number>(0);

    const [groupData, setGroupData] = React.useState([]);
    const [createItems, setCreateItems] = React.useState<boolean>();
    const [createPO, setCreatePO] = React.useState<boolean>();

    const [creationIndicator, setCreationIndicator] = React.useState<CreationIndicator>();
    const [exportType, setExportType] = React.useState<string>("");

    const [text, setText] = React.useState<object>({});
    const [menuItem, setMenuItem] = React.useState<string>("");
    const [dates, setDates] = React.useState<object>({});
    const stepsRef = React.useRef<Array<any>>([]);

    const handleTextChange = React.useCallback((name, _event, value) => {
        setText((prevValue) => {
            selectedOptions.current[name.field] = {
                ...selectedOptions.current[name.field],
                option_value: { string_value: value },
            };
            return { ...prevValue, [name.field]: value };
        });
    }, []);

    const handleMenuSelectChange = React.useCallback((values, event, _index, selectedColumn) => {
        const hasDependantOption = values.options.some((option) => option.tag === selectedColumn);
        if (hasDependantOption) {
            // Reset the second menu selection when we have to enforce dependant selection
            handleBulkMenuSelectChange(values, undefined, undefined, undefined);
        } else {
            /* We need to fetch the display value instead of internal key to be used in exports/logs */
            let selectedNameColumn = selectedColumn;
            values?.options?.some((val) => {
                if (val?.key === selectedColumn) {
                    selectedNameColumn = val.value;
                }
            });
            handleBulkMenuSelectChange(values, undefined, undefined, selectedNameColumn);
        }
        setMenuItem(selectedColumn);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleBulkMenuSelectChange = React.useCallback((values, event, _index, selectedColumn) => {
        setText((prevValue) => {
            selectedOptions.current[values.field] = {
                ...selectedOptions.current[values.field],
                option_value: { string_value: selectedColumn },
            };
            return { ...prevValue, [values.field]: selectedColumn };
        });
    }, []);

    const handleDateChange = React.useCallback(
        (name, _event, value) => {
            setDates((prevValue) => {
                selectedOptions.current[name.field] = {
                    ...selectedOptions.current[name.field],
                    option_value: { date_value: { year: value.year(), month: value.month() + 1, day: value.date() } },
                };
                return { ...prevValue, [name.field]: value };
            });
        },
        [setDates]
    );

    const configMerchant = React.useMemo(() => {
        return merchantFeaturePreference?.data_export?.exports?.find(
            (element) => element.data_type === AllowedExportTypeOptions.PURCHASE_ORDER
        )?.form?.fields;
    }, [merchantFeaturePreference]);

    const selectionConfig = React.useMemo(() => {
        return (
            merchantFeaturePreference?.data_export?.exports?.find(
                (element) => element.data_type === AllowedExportTypeOptions.PURCHASE_ORDER
            )?.selection ?? {
                options: Object.values(AllowedExportTypeOptions),
            }
        );
    }, [merchantFeaturePreference]);

    React.useEffect(() => {
        if (merchantId) {
            fetchMerchantAllocationDimensions(merchantId);
        }
    }, [merchantId]); // eslint-disable-line react-hooks/exhaustive-deps

    const allocationDimensionsOptions = React.useMemo(() => {
        return merchantAllocationDimensions.map((merchantAllocationDimension) => {
            const activeAllocationDimensionValues = merchantAllocationDimension.dimension_values?.reduce(
                (acc, dimensionValue) => {
                    if (dimensionValue.active && dimensionValue.included) {
                        acc.push({
                            text: dimensionValue.name,
                            key: dimensionValue.id,
                            id: dimensionValue.id,
                            allocationDimensionId: merchantAllocationDimension.id,
                        });
                    }
                    return acc;
                },
                []
            );
            return {
                id: merchantAllocationDimension.id.replace(ALLOCATION_DIMENSION_ID_PREFIX, ""),
                name: merchantAllocationDimension.name,
                values: activeAllocationDimensionValues,
            };
        });
    }, [merchantAllocationDimensions]);

    const allAllocationDimensionValues = React.useMemo(
        () =>
            allocationDimensionsOptions.reduce(
                (acc, curr) => ({ ...acc, [curr.id]: curr.values.map((value) => value.key) }),
                {}
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    const [selectedAllocationDimensionValues, setSelectedAllocationDimensionValues] = React.useState<{
        [allocationDimensionId: string]: string[];
    }>(allAllocationDimensionValues);
    const allowUpdateByDataType = (allowedExportTypeOptions: AllowedExportTypeOptions): boolean => {
        return merchantFeaturePreference?.data_export?.exports?.find(
            (element) => element.data_type === allowedExportTypeOptions
        )?.allow_update;
    };
    const createItemsAndPO = createItems && createPO;
    const allowUpdateItem = allowUpdateByDataType(AllowedExportTypeOptions.ITEM);
    const allowUpdatePO = allowUpdateByDataType(AllowedExportTypeOptions.PURCHASE_ORDER);
    let allowUpdate = false;

    if (createItemsAndPO) {
        allowUpdate = allowUpdateItem || allowUpdatePO;
    } else if (createItems) {
        allowUpdate = allowUpdateItem;
    } else {
        allowUpdate = allowUpdatePO;
    }

    React.useEffect(() => {
        if (createPO) {
            configMerchant
                ?.filter((value) => value.kind === ValueTypes.STRING)
                .forEach((data) => {
                    selectedOptions.current[data.field] = {
                        key: data.field,
                        display_name: data.name,
                    };
                    setText((prevValue) => {
                        return { ...prevValue, [data.field]: data.value };
                    });
                });
            configMerchant
                ?.filter((value) => value.kind === ValueTypes.DATE)
                .forEach((data) => {
                    selectedOptions.current[data.field] = {
                        key: data.field,
                        display_name: data.name,
                        option_value: {
                            date_value: {
                                year: moment(data.value).year(),
                                month: moment(data.value).month() + 1,
                                day: moment(data.value).date(),
                            },
                        },
                    };
                    setDates((prevValue) => {
                        return { ...prevValue, [data.field]: data.value };
                    });
                });

            const groupedRows = configMerchant
                ?.map((e, i) => {
                    return i % 2 === 0 ? configMerchant?.slice(i, i + 2) : null;
                })
                .filter((e) => {
                    return e;
                });

            setGroupData(groupedRows);

            // Set default selected dimension values
            setSelectedAllocationDimensionValues(
                allocationDimensionsOptions.reduce((acc, allocationDimensionsOption) => {
                    acc[allocationDimensionsOption.id] = allocationDimensionsOption.values.map((value) => value.key);
                    return acc;
                }, {})
            );
        } else {
            selectedOptions.current = {};
        }
        if (allowUpdate) {
            onFetchMoreRows();
        }
    }, [configMerchant, createPO, allocationDimensionsOptions, onFetchMoreRows, allowUpdate]);

    const handleChangeAllocationDimensionValue = (allocationDimension) => (selectedKeys) => {
        const selectedAllocationDimensionIds = allocationDimension.values.reduce((acc, dimensionValue) => {
            if (selectedKeys.includes(dimensionValue.id)) {
                acc.push(dimensionValue.id);
            }
            return acc;
        }, []);

        setSelectedAllocationDimensionValues((existing) => {
            return {
                ...existing,
                [allocationDimension.id]: selectedAllocationDimensionIds,
            };
        });
    };

    const handleConfirmClose = React.useCallback(
        (_e) => {
            onConfirmClose();
        },
        [onConfirmClose]
    );
    const isMultipleSelectionAllowed = merchantFeaturePreference?.data_export?.allow_multiple;

    const handleOptionschange = React.useCallback(
        (e, status) => {
            const value = e.target.value;
            if (value === "PO") {
                if (e.target.checked && exportItemPO !== undefined && !!exportItemPO.po) {
                    exportItemPO.po.active = true;
                } else if (e.target.checked === false && exportItemPO !== undefined && !!exportItemPO.po) {
                    exportItemPO.po.active = false;
                }
            } else if (value === "Item") {
                if (e.target.checked && exportItemPO !== undefined && !!exportItemPO.item) {
                    exportItemPO.item.active = true;
                } else if (e.target.checked === false && exportItemPO !== undefined && !!exportItemPO.item) {
                    exportItemPO.item.active = false;
                }
            }
            if (onFetchItemPO) {
                onFetchItemPO(exportItemPO);
            }
            switch (value) {
                case exportOptions.item.name:
                    setCreateItems(!isMultipleSelectionAllowed || status);
                    !isMultipleSelectionAllowed && setCreatePO(false);
                    break;
                case exportOptions.po.name:
                    setCreatePO(!isMultipleSelectionAllowed || status);
                    !isMultipleSelectionAllowed && setCreateItems(false);
                    break;
                default:
                    break;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setCreateItems, setCreatePO, setExportType, isMultipleSelectionAllowed, onFetchItemPO]
    );

    const handleCreateUpdateChange = (e) => setCreationIndicator(e.target.value);

    React.useEffect(() => {
        let exportType = createItems && createPO ? `${exportOptions.item.name}, ${exportOptions.po.name}` : "";
        if (createItems && !createPO) {
            exportType = exportOptions.item.name;
        } else if (!createItems && createPO) {
            exportType = exportOptions.po.name;
        }
        setExportType(exportType);
    }, [createItems, createPO, exportOptions.item, exportOptions.po]);

    const handleConfirm = React.useCallback(
        (exportType: string) => {
            const selectedItemIds = (gridApi.getSelectedRows() || []).map((item) => ({
                string_value: item.fields.id.string_value,
            }));
            let creationIndicatorToBeSent = creationIndicator;

            // If creation indicator is unset and we allow update, we know it's a create intention
            if (!creationIndicatorToBeSent && allowUpdate) {
                creationIndicatorToBeSent = CreationIndicator.CREATE_EXPORT;
            }

            onCreateCollectionExportItemsPO({
                selectedOptions: Object.values(selectedOptions.current),
                selectedAllocationDimensionValues,
                selectedItemIds,
                exportType,
                collectionId,
                merchantId,
                exportOptions,
                creationIndicator: creationIndicatorToBeSent,
                allowUpdate,
            });
            onBulkItemsClearAll();
            onConfirmClose();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onConfirmClose, selectedAllocationDimensionValues, merchantId, creationIndicator, allowUpdate]
    );

    const handleValidateItems = React.useCallback((mode) => onValidateItems?.(mode, false), [onValidateItems]);
    stepsRef.current = [
        //show Export Options
        {
            path: "[strong]Export to Merchant[strong] [span] - Choose Export Type[span]",
            confirmLabel: "Next",
            cancelLabel: "Cancel",
            disabledConfirm() {
                return !(createItems || createPO);
            },
            onNext: validateItems({ createItems, createPO, allowUpdate, hasExportLogs, exportItemPO })
                ? handleValidateItems.bind(null, ValidationModes.ValidateItems)
                : undefined,
        },
        //show Dimensions for PO
        {
            path: "[strong]Export to Merchant[strong] [span] - Choose Dimensions[span]",
            confirmLabel: "Next",
            cancelLabel: "Cancel",
            disabledConfirm() {
                return enableDimensionStep;
            },
        },
        //show PO Details
        {
            path: "[strong]Export to Merchant[strong] [span] - Define |exportNamePO| Details[span]",
            confirmLabel: "Create |exportNamePO|",
            cancelLabel: "Cancel",
            disabledConfirm() {
                // We need to check that all mandatory fields are populated.
                // Dates are automatically populated with the current date, so we dont need to check those
                return Object.keys(text).some((name) => {
                    return (
                        (text[name] === undefined || text[name].trim() === "") &&
                        !configMerchant.some((data) => {
                            return name === data.field && data.is_optional;
                        })
                    );
                });
            },
            onNext: handleValidateItems.bind(null, createItems ? undefined : ValidationModes.ValidatePOs),
        },
        //show Affinity Options
        {
            path: "[strong]Create or Add to |exportNameItem| [strong]",
            confirmLabel: "Export",
            cancelLabel: "Cancel",
            disabledConfirm() {
                return !creationIndicator;
            },
            onNext: handleValidateItems.bind(null, ValidationModes.ValidateItems),
        },
    ];
    const isThereAtLeastOneDimensionValueChecker = (
        selectedAllocationDimensionValues,
        merchantAllocationDimensions
    ) => {
        return Object.keys(selectedAllocationDimensionValues).every((selecteddAllocationDimensionKey) => {
            const currentMerchantAllocationDimension = merchantAllocationDimensions.find(
                (merchantAllocationDimension) => merchantAllocationDimension.id === selecteddAllocationDimensionKey
            );
            let dimensionValues = [];
            if (currentMerchantAllocationDimension) {
                dimensionValues = currentMerchantAllocationDimension.dimension_values.filter(
                    (dimension_value) => dimension_value.active
                );
            }

            return selectedAllocationDimensionValues[selecteddAllocationDimensionKey].length !== dimensionValues.length;
        });
    };

    const handleNext = React.useCallback(
        () => {
            const onlyCreateItems = createItems && !createPO;
            setActiveStep((prevActiveStep) => {
                const steps = stepsRef.current;
                const validationResult = steps[prevActiveStep].onNext?.();
                // to show create or Update modal if we only have Create Items or PO are selected,
                // and we validate to that we are not in step 3 to prevent the modal shows again
                if ((prevActiveStep === 2 || onlyCreateItems) && hasExportLogs && allowUpdate && prevActiveStep !== 3) {
                    return 3;
                }

                // to enter also if the create or Update modal is showing to export
                if (prevActiveStep === steps.length - 2 || onlyCreateItems || prevActiveStep === 3) {
                    if (validationResult !== undefined) {
                        const { erroredItems, warnedItems } = validationResult;
                        if (!erroredItems.length && !warnedItems.length) {
                            onlyCreateItems && handleConfirm(exportType);
                            !warnedItems.length && createPO && handleConfirm(exportType);
                        } else if (warnedItems.length) {
                            const selectedItemIds = (gridApi.getSelectedRows() || [])
                                .filter(
                                    (item) =>
                                        !erroredItems.some(
                                            (erroredItem) => amphibiousGetIn(item, Item.FIELDS.ID) === erroredItem
                                        )
                                )
                                .map((element) => ({ string_value: element.fields.id.string_value }));
                            const exportItemsCollection = {
                                selectedOptions: Object.values(selectedOptions.current),
                                selectedAllocationDimensionValues,
                                selectedItemIds,
                                exportType,
                                collectionId,
                                merchantId,
                                exportOptions,
                            };
                            onExportItemsCollection(exportItemsCollection);
                        }
                        validationResult.warnedItems === 0 && onBulkItemsClearAll();
                        onConfirmClose();
                    }
                    return prevActiveStep;
                }
                return prevActiveStep + 1;
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            setActiveStep,
            exportType,
            createItems,
            createPO,
            selectedAllocationDimensionValues,
            hasExportLogs,
            creationIndicator,
        ]
    );

    const isThereAtLeastOneDimensionValue = React.useCallback(
        () => isThereAtLeastOneDimensionValueChecker(selectedAllocationDimensionValues, merchantAllocationDimensions),
        [selectedAllocationDimensionValues, merchantAllocationDimensions]
    );
    const enableDimensionStep = React.useMemo(() => {
        let disabled = !(Object.keys(selectedAllocationDimensionValues).length >= allocationDimensionsOptions?.length);
        if (!disabled && merchantAllocationDimensions.length > 0) {
            disabled = !isThereAtLeastOneDimensionValue();
        }
        return disabled;
    }, [selectedAllocationDimensionValues, merchantAllocationDimensions]); // eslint-disable-line react-hooks/exhaustive-deps

    const columnsValue = (menuItems) => {
        const options = menuItems.options.filter((element) => element.tag === menuItems.field);
        return options.map((item) => (
            <MenuItem
                key={item.key}
                value={item.key}
                primaryText={item.value}
                style={muiStyles.subMenuItem}
                data-test={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.MODAL.SELECT_FIRST_OPTION}
            />
        ));
    };

    return (
        <>
            <Confirm
                disabledConfirm={stepsRef.current[activeStep].disabledConfirm()}
                contentStyle={{ maxWidth: "500px" }}
                title={
                    <div
                        data-test={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.MODAL.DIALOG_TITLE}
                        style={muiStyles.titleContent}
                    >
                        <FormatMessage
                            path={stepsRef.current[activeStep].path}
                            variables={{ exportNamePO: exportOptions.po.name, exportNameItem: exportOptions.item.name }}
                            elements={{
                                strong: <strong />,
                                span: <span style={{ fontSize: "18px", color: theme.palette.darkGrey }} />,
                            }}
                        />
                    </div>
                }
                open={exportToMerchantDialogOpen}
                onConfirm={handleNext}
                onCancel={handleConfirmClose}
                confirmLabel={formatMessage(stepsRef.current[activeStep].confirmLabel, {
                    exportNamePO: exportOptions.po.name,
                })}
                cancelLabel={formatMessage(stepsRef.current[activeStep].cancelLabel)}
            >
                <div className="exportToMerchantContainer" {...styles}>
                    {activeStep === 0 && (
                        <Form>
                            <div className="optionsContainer">
                                {selectionConfig.options.includes(AllowedExportTypeOptions.ITEM) &&
                                    (isMultipleSelectionAllowed ? (
                                        <Checkbox
                                            className="optionsCheck"
                                            checked={createItems}
                                            value={exportOptions.item.name}
                                            onCheck={handleOptionschange}
                                            label={formatMessage("Create |exportNameItem|", {
                                                exportNameItem: exportOptions.item.name,
                                            })}
                                            data-test={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.MODAL.CREATE_ITEMS_CBX}
                                        />
                                    ) : (
                                        <RadioButton
                                            className="optionsCheck"
                                            name="exportTypeSelection"
                                            checked={createItems}
                                            value={exportOptions.item.name}
                                            onCheck={handleOptionschange}
                                            label={formatMessage("Create |exportNameItem|", {
                                                exportNameItem: exportOptions.item.name,
                                            })}
                                            data-test={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.MODAL.CREATE_ITEMS_RDB}
                                        />
                                    ))}
                                {selectionConfig.options.includes(AllowedExportTypeOptions.PURCHASE_ORDER) &&
                                    exportOptions.po.active &&
                                    (isMultipleSelectionAllowed ? (
                                        <Checkbox
                                            className="optionsCheck"
                                            name="exportTypeSelection"
                                            checked={createPO}
                                            value={exportOptions.po.name}
                                            onCheck={handleOptionschange}
                                            label={formatMessage("Create |exportNamePO|", {
                                                exportNamePO: exportOptions.po.name,
                                            })}
                                            data-test={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.MODAL.CREATE_PO_CBX}
                                        />
                                    ) : (
                                        <RadioButton
                                            className="optionsCheck"
                                            checked={createPO}
                                            value={exportOptions.po.name}
                                            onCheck={handleOptionschange}
                                            label={formatMessage("Create |exportNamePO|", {
                                                exportNamePO: exportOptions.po.name,
                                            })}
                                            data-test={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.MODAL.CREATE_PO_RDB}
                                        />
                                    ))}
                            </div>
                        </Form>
                    )}
                    {activeStep === 1 &&
                        allocationDimensionsOptions.map((allocationDimension) => (
                            <Theme key={allocationDimension.id}>
                                <Fabric>
                                    <Dropdown
                                        key={allocationDimension.id}
                                        label={allocationDimension.name}
                                        options={allocationDimension.values}
                                        selectedKey={allocationDimension.values
                                            .filter((value) =>
                                                selectedAllocationDimensionValues[allocationDimension.id]?.includes(
                                                    value.key
                                                )
                                            )
                                            .map((value) => value.key)}
                                        testId={FOLDERS.MULTI_SELECT.DROPDOWN}
                                        onChange={handleChangeAllocationDimensionValue(allocationDimension)}
                                        multiSelect
                                        isSearchable
                                        selectAllText={formatMessage("Select All")}
                                        searchBoxPlaceholder={formatMessage("Search")}
                                        noResultsText={formatMessage("No results found")}
                                    />
                                </Fabric>
                            </Theme>
                        ))}
                    {activeStep === 2 && (
                        <Form>
                            {(groupData ?? []).map((data, index) => (
                                <div className="formGroupContainer" key={index}>
                                    {data.map((values, i) => (
                                        <div key={i}>
                                            {values.kind === ValueTypes.STRING && !values.options.length && (
                                                <>
                                                    <TextField
                                                        labelClassName="title"
                                                        label={formatMessage(values.name)}
                                                        name={values.field}
                                                        fullWidth={true}
                                                        large={true}
                                                        type="text"
                                                        value={text[values.field] || ""}
                                                        autoComplete="off"
                                                        onChange={handleTextChange.bind(this, values)}
                                                        maxLength={safeParseInt(values.max_length, 0)}
                                                        dataTest={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.MODAL.TEXT_FIELD}
                                                    />
                                                    <p className="message">
                                                        <strong>
                                                            {formatMessage("Maximum |maxLength| characters", {
                                                                maxLength: values.max_length,
                                                            })}{" "}
                                                        </strong>
                                                    </p>
                                                    {values.description.length > 0 && (
                                                        <p className="message">
                                                            <strong>{values.description}</strong>
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                            {values.kind === ValueTypes.DATE && (
                                                <>
                                                    <DatePicker
                                                        labelClassName="title"
                                                        label={formatMessage(values.name)}
                                                        name={values.field}
                                                        large={true}
                                                        value={dates[values.field] || moment()}
                                                        onChange={handleDateChange.bind(this, values)}
                                                        dataTest={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.MODAL.DATE_FIELD}
                                                    />
                                                    {values.description.length > 0 && (
                                                        <p className="message">
                                                            <strong>{values.description}</strong>
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                            {values.kind === ValueTypes.STRING && !!values.options.length && (
                                                <Select
                                                    labelClassName="selectTitle"
                                                    name={values.field}
                                                    label={formatMessage(values.name)}
                                                    autoWidth={true}
                                                    large={true}
                                                    value={menuItem}
                                                    onChange={handleMenuSelectChange.bind(this, values)}
                                                    data-test={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.MODAL.SELECT_FIRST}
                                                >
                                                    {columnsValue(values)}
                                                </Select>
                                            )}

                                            {values.kind === ValueTypes.STRING &&
                                                !!values.options.length &&
                                                menuItem && (
                                                    <DependantSelectOptions
                                                        field={values}
                                                        value={text[values.field] || ""}
                                                        onSelect={handleBulkMenuSelectChange.bind(this, values)}
                                                        parentValue={menuItem}
                                                    />
                                                )}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </Form>
                    )}
                    {activeStep === 3 && (
                        <Form>
                            <div className="optionsContainer">
                                <>
                                    <p className="message">
                                        <strong>
                                            {formatMessage(
                                                "Your assortment has previously been exported at least once. Would you like this export to create a new plan in |exportNameItem| or add to the most recently created |exportNameItem| Plan?",
                                                { exportNameItem: exportOptions.item.name }
                                            )}
                                        </strong>
                                    </p>
                                    <RadioButton
                                        className="optionsCheck"
                                        name={CreationIndicator.UPDATE_EXPORT}
                                        checked={creationIndicator === CreationIndicator.UPDATE_EXPORT}
                                        value={CreationIndicator.UPDATE_EXPORT}
                                        onCheck={handleCreateUpdateChange}
                                        label={formatMessage("Add to latest |exportNameItem| Plan", {
                                            exportNameItem: exportOptions.item.name,
                                        })}
                                        data-test={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.UPDATE_EXPORT}
                                    />
                                    <RadioButton
                                        className="optionsCheck"
                                        name={CreationIndicator.CREATE_EXPORT}
                                        checked={creationIndicator === CreationIndicator.CREATE_EXPORT}
                                        value={CreationIndicator.CREATE_EXPORT}
                                        onCheck={handleCreateUpdateChange}
                                        label={formatMessage("Create a new |exportNameItem| Plan", {
                                            exportNameItem: exportOptions.item.name,
                                        })}
                                        data-test={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.CREATE_EXPORT}
                                    />
                                </>
                            </div>
                        </Form>
                    )}
                </div>
            </Confirm>
        </>
    );
};

interface IDependantSelectOptionsProps {
    field: nuorder.collection.form.Form.Field;
    onSelect: React.ComponentProps<typeof MenuItem>["onChange"];
    value?: string;
    parentValue: string;
}
const DependantSelectOptions = ({ field, value, onSelect, parentValue }: IDependantSelectOptionsProps) => {
    const options = field.options.filter((item) => item.tag === parentValue);
    const hasOptions = options.length > 0;
    return (
        hasOptions && (
            <>
                <Select
                    labelClassName="selectTitle"
                    name={field.field}
                    autoWidth={true}
                    large={true}
                    value={value}
                    onChange={onSelect}
                    data-test={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.MODAL.SELECT_SECOND}
                >
                    {options.map((item) => (
                        <MenuItem
                            key={item.key}
                            value={item.key}
                            primaryText={item.value}
                            style={muiStyles.subMenuItem}
                            data-test={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.MODAL.SELECT_SECOND_OPTION}
                        />
                    ))}
                </Select>
                {field.description.length > 0 && (
                    <p className="message">
                        <strong>{field.description}</strong>
                    </p>
                )}
            </>
        )
    );
};
